export type WorkInput =
  | { type: "text"; text: string }
  | { type: "image"; url: string; detail: null }
  | { type: "localImage"; path: string; detail: null }
  | { type: "mention"; name: string; path: string }
  | { type: "skill"; name: string; path: string };

export type WorkExecutionPolicy = "read-only" | "workspace" | "full-access";
export type WorkApprovalPolicy = "on-request" | "never";
export type WorkThreadSandbox = "read-only" | "workspace-write" | "danger-full-access";
export type WorkTurnSandboxPolicy =
  | { type: "readOnly"; networkAccess: false }
  | {
      type: "workspaceWrite";
      writableRoots: string[];
      networkAccess: false;
      excludeSlashTmp: false;
      excludeTmpdirEnvVar: false;
    }
  | { type: "dangerFullAccess" };

export type WorkThreadExecutionParams = {
  executionPolicy: WorkExecutionPolicy;
  approvalPolicy: WorkApprovalPolicy;
  sandbox: WorkThreadSandbox;
};

export type WorkTurnExecutionParams = {
  executionPolicy: WorkExecutionPolicy;
  approvalPolicy: WorkApprovalPolicy;
  sandboxPolicy: WorkTurnSandboxPolicy;
};

export function normalizeWorkExecutionPolicy(value: unknown, legacyReadOnly = false): WorkExecutionPolicy {
  if (value === "read-only" || value === "workspace" || value === "full-access") return value;
  if (legacyReadOnly) return "read-only";
  // `auto` was the pre-three-tier name for the server-imposed workspace mode.
  return "workspace";
}

export function threadExecutionPolicyParams(policy: WorkExecutionPolicy): WorkThreadExecutionParams {
  if (policy === "read-only") {
    return { executionPolicy: policy, approvalPolicy: "on-request", sandbox: "read-only" };
  }
  if (policy === "full-access") {
    return { executionPolicy: policy, approvalPolicy: "never", sandbox: "danger-full-access" };
  }
  return { executionPolicy: policy, approvalPolicy: "on-request", sandbox: "workspace-write" };
}

export function turnExecutionPolicyParams(
  policy: WorkExecutionPolicy,
  workspacePath: string,
): WorkTurnExecutionParams {
  if (policy === "read-only") {
    return {
      executionPolicy: policy,
      approvalPolicy: "on-request",
      sandboxPolicy: { type: "readOnly", networkAccess: false },
    };
  }
  if (policy === "full-access") {
    return {
      executionPolicy: policy,
      approvalPolicy: "never",
      sandboxPolicy: { type: "dangerFullAccess" },
    };
  }
  return {
    executionPolicy: policy,
    approvalPolicy: "on-request",
    sandboxPolicy: {
      type: "workspaceWrite",
      writableRoots: [workspacePath],
      networkAccess: false,
      excludeSlashTmp: false,
      excludeTmpdirEnvVar: false,
    },
  };
}

export type WorkSubmission =
  | {
      intent: "start";
      method: "turn/start";
      params: {
        threadId: string;
        input: WorkInput[];
        cwd: string;
        clientUserMessageId: string;
        model?: string;
        effort?: string;
        executionPolicy?: WorkExecutionPolicy;
        approvalPolicy?: WorkApprovalPolicy;
        sandboxPolicy?: WorkTurnSandboxPolicy;
      };
    }
  | {
      intent: "steer";
      method: "turn/steer";
      params: {
        threadId: string;
        input: WorkInput[];
        expectedTurnId: string;
        clientUserMessageId: string;
      };
    };

export function buildWorkSubmission(options: {
  threadId: string;
  activeTurnId: string | null;
  cwd: string;
  input: WorkInput[];
  clientUserMessageId: string;
  model?: string;
  effort?: string;
  executionPolicy?: WorkExecutionPolicy;
}): WorkSubmission {
  if (options.activeTurnId) {
    return {
      intent: "steer",
      method: "turn/steer",
      params: {
        threadId: options.threadId,
        input: options.input,
        expectedTurnId: options.activeTurnId,
        clientUserMessageId: options.clientUserMessageId,
      },
    };
  }
  return {
    intent: "start",
    method: "turn/start",
    params: {
      threadId: options.threadId,
      input: options.input,
      cwd: options.cwd,
      clientUserMessageId: options.clientUserMessageId,
      ...(options.model ? { model: options.model } : {}),
      ...(options.effort ? { effort: options.effort } : {}),
      ...(options.executionPolicy
        ? turnExecutionPolicyParams(options.executionPolicy, options.cwd)
        : {}),
    },
  };
}

export function buildWorkInterrupt(threadId: string, turnId: string): {
  method: "turn/interrupt";
  params: { threadId: string; turnId: string };
} {
  return { method: "turn/interrupt", params: { threadId, turnId } };
}

export type OptimisticDelivery = "pending" | "accepted" | "confirmed" | "failed";
export type OptimisticDeliveryEvent = "rpcAccepted" | "serverConfirmed" | "rpcFailed";

export function transitionOptimisticDelivery(
  current: OptimisticDelivery,
  event: OptimisticDeliveryEvent,
): OptimisticDelivery {
  if (event === "serverConfirmed") return "confirmed";
  if (event === "rpcFailed" && current !== "confirmed") return "failed";
  if (event === "rpcAccepted" && current === "pending") return "accepted";
  return current;
}

export type WorkTerminalStatus = "completed" | "interrupted" | "failed";
export type WorkStage =
  | "idle"
  | "starting"
  | "active"
  | "thinking"
  | "planning"
  | "command"
  | "files"
  | "tool"
  | "research"
  | "waiting"
  | "responding"
  | "stopping"
  | "completed"
  | "interrupted"
  | "failed"
  | "recoverableError";

export type WorkPlanStatus = "pending" | "inProgress" | "completed";
export type WorkPlanStep = { step: string; status: WorkPlanStatus };

export type WorkControlSnapshot = {
  threadId: string | null;
  activeTurnId: string | null;
  isActive: boolean;
  submissionPending: boolean;
  interruptPending: boolean;
  canStop: boolean;
  composerIntent: "start" | "steer";
  composerPlaceholder: string;
  composerHint: string;
  sendLabel: string;
  stopLabel: string;
  stage: WorkStage;
  stageLabel: string;
  guidance: string;
  detail: string;
  plan: WorkPlanStep[];
  terminalStatus: WorkTerminalStatus | null;
  error: string;
  awaitingApproval: boolean;
  lastActivityAt: number | null;
};

type Activity = { id: string; type: string; stage: WorkStage; detail: string };

type ThreadState = {
  activeTurnId: string | null;
  terminalTurnId: string | null;
  terminalTurnIds: Set<string>;
  submissionPending: boolean;
  interruptPending: boolean;
  interruptAccepted: boolean;
  stage: WorkStage;
  guidance: string;
  detail: string;
  plan: WorkPlanStep[];
  terminalStatus: WorkTerminalStatus | null;
  error: string;
  activities: Map<string, Activity>;
  awaitingApprovalMethod: string | null;
  awaitingApprovalTurnId: string | null;
  lastActivityAt: number | null;
};

type WorkEventData = Record<string, unknown>;
type Listener = (snapshot: WorkControlSnapshot) => void;

const STAGE_LABELS: Record<WorkStage, string> = {
  idle: "准备就绪",
  starting: "正在启动任务",
  active: "任务进行中",
  thinking: "正在分析任务",
  planning: "正在规划步骤",
  command: "正在执行命令",
  files: "正在处理文件",
  tool: "正在调用工具",
  research: "正在检索资料",
  waiting: "等待你的确认",
  responding: "正在整理结果",
  stopping: "正在停止任务",
  completed: "任务已完成",
  interrupted: "任务已停止",
  failed: "任务未完成",
  recoverableError: "请求未送达",
};

const ACTIVITY_PRIORITY: Record<WorkStage, number> = {
  idle: 0,
  completed: 0,
  interrupted: 0,
  failed: 0,
  recoverableError: 1,
  active: 2,
  responding: 3,
  thinking: 4,
  planning: 5,
  research: 6,
  tool: 7,
  files: 8,
  command: 9,
  waiting: 10,
  starting: 11,
  stopping: 12,
};

const MAX_TERMINAL_TURN_IDS = 128;

function rememberTerminalTurn(state: ThreadState, turnId: string): void {
  if (!turnId) return;
  // Set insertion order gives us a small LRU-style window. Re-observing a
  // terminal turn keeps it protected while bounding long-lived thread state.
  state.terminalTurnIds.delete(turnId);
  state.terminalTurnIds.add(turnId);
  while (state.terminalTurnIds.size > MAX_TERMINAL_TURN_IDS) {
    const oldest = state.terminalTurnIds.values().next().value;
    if (typeof oldest !== "string") break;
    state.terminalTurnIds.delete(oldest);
  }
}

function isTerminalTurn(state: ThreadState, turnId: string | null | undefined): boolean {
  return Boolean(turnId && state.terminalTurnIds.has(turnId));
}

function emptyState(): ThreadState {
  return {
    activeTurnId: null,
    terminalTurnId: null,
    terminalTurnIds: new Set(),
    submissionPending: false,
    interruptPending: false,
    interruptAccepted: false,
    stage: "idle",
    guidance: "输入指令后将开始一个新任务。",
    detail: "",
    plan: [],
    terminalStatus: null,
    error: "",
    activities: new Map(),
    awaitingApprovalMethod: null,
    awaitingApprovalTurnId: null,
    lastActivityAt: null,
  };
}

function record(value: unknown): WorkEventData | null {
  return value && typeof value === "object" ? value as WorkEventData : null;
}

function stringValue(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function terminalStatus(value: unknown): WorkTerminalStatus | null {
  return value === "completed" || value === "interrupted" || value === "failed" ? value : null;
}

const ACTIVE_TURN_EVIDENCE_TOPICS = new Set([
  "turn/plan/updated",
  "turn.plan.updated",
  "codex.plan.updated",
  "item/started",
  "codex.item.started",
  "item/completed",
  "codex.item.completed",
  "turn/diff/updated",
  "item/fileChange/patchUpdated",
  "item/commandExecution/outputDelta",
  "item/commandExecution/terminalInteraction",
  "command/exec/outputDelta",
  "item/mcpToolCall/progress",
  "model/safetyBuffering/updated",
  "model/rerouted",
  "model/verification",
]);

function planStatus(value: unknown): WorkPlanStatus | null {
  if (value === "pending" || value === "inProgress" || value === "completed") return value;
  if (value === "in_progress") return "inProgress";
  return null;
}

function normalizePlan(value: unknown): WorkPlanStep[] {
  if (!Array.isArray(value)) return [];
  const result: WorkPlanStep[] = [];
  for (const candidate of value) {
    const entry = record(candidate);
    const step = stringValue(entry?.step).trim();
    const status = planStatus(entry?.status);
    if (step && status) result.push({ step, status });
  }
  return result;
}

function concise(value: unknown, maxLength = 120): string {
  const text = Array.isArray(value)
    ? value.map((part) => typeof part === "string" ? part : "").filter(Boolean).join(" ")
    : stringValue(value);
  const normalized = text.replace(/\s+/g, " ").trim();
  return normalized.length > maxLength ? `${normalized.slice(0, maxLength - 1)}…` : normalized;
}

function fileDetail(item: WorkEventData): string {
  const changes = Array.isArray(item.changes) ? item.changes : [];
  if (!changes.length) return "正在准备文件变更";
  const paths = changes
    .map((change) => concise(record(change)?.path, 56))
    .filter(Boolean);
  if (paths.length === 1) return `正在处理 ${paths[0]}`;
  return `正在处理 ${paths.length} 个文件`;
}

function toolDetail(item: WorkEventData): string {
  const server = concise(item.server, 36);
  const tool = concise(item.tool, 48);
  return [server, tool].filter(Boolean).join(" · ") || "正在等待工具返回";
}

function activityFor(item: WorkEventData): Activity | null {
  const id = stringValue(item.id);
  const type = stringValue(item.type);
  if (!id || !type || type === "userMessage") return null;
  switch (type) {
    case "reasoning":
      return { id, type, stage: "thinking", detail: "正在分析目标、上下文与约束" };
    case "plan":
      return { id, type, stage: "planning", detail: "正在形成可执行步骤" };
    case "commandExecution":
      return { id, type, stage: "command", detail: concise(item.command) || "正在运行后台命令" };
    case "fileChange":
      return { id, type, stage: "files", detail: fileDetail(item) };
    case "mcpToolCall":
    case "dynamicToolCall":
    case "collabToolCall":
    case "collabAgentToolCall":
      return { id, type, stage: "tool", detail: toolDetail(item) };
    case "webSearch":
      return { id, type, stage: "research", detail: concise(item.query) || "正在检索相关资料" };
    case "contextCompaction":
      return { id, type, stage: "thinking", detail: "正在整理较长的任务上下文" };
    case "agentMessage":
      return {
        id,
        type,
        stage: item.phase === "final_answer" ? "responding" : "active",
        detail: item.phase === "final_answer" ? "正在整理最终结果" : "正在反馈当前进展",
      };
    default:
      return { id, type, stage: "active", detail: "正在继续执行当前任务" };
  }
}

function errorMessage(value: unknown): string {
  const candidate = record(value);
  return stringValue(candidate?.message) || stringValue(value);
}

export class WorkControlStore {
  private selectedThreadId: string | null = null;
  private readonly threads = new Map<string, ThreadState>();
  private readonly listeners = new Set<Listener>();

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  selectThread(threadId: string | null): void {
    this.selectedThreadId = threadId;
    if (threadId) this.state(threadId);
    this.emit();
  }

  activeTurnId(threadId: string): string | null {
    return this.state(threadId).activeTurnId;
  }

  beginSubmission(threadId: string): boolean {
    const state = this.state(threadId);
    if (state.submissionPending || state.interruptPending) return false;
    state.submissionPending = true;
    state.terminalStatus = null;
    state.error = "";
    if (!state.activeTurnId) {
      state.stage = "starting";
      state.guidance = "请求正在发送，等待服务器确认任务标识。";
      state.detail = "";
    } else {
      state.guidance = "正在把补充引导送入当前任务。";
      state.detail = "不会创建新的任务轮次";
    }
    this.emitIfSelected(threadId);
    return true;
  }

  submissionAccepted(threadId: string, turnId: string, intent: WorkSubmission["intent"]): void {
    const state = this.state(threadId);
    if (isTerminalTurn(state, turnId)) {
      // The authoritative completion event can legitimately overtake the RPC reply.
      // Never revive a turn that the server has already declared terminal.
      state.submissionPending = false;
      this.emitIfSelected(threadId);
      return;
    }
    const preserveProgress = state.activeTurnId === turnId
      && ["planning", "command", "files", "tool", "research", "waiting", "responding"].includes(state.stage);
    state.submissionPending = false;
    state.activeTurnId = turnId;
    state.terminalTurnId = null;
    state.terminalStatus = null;
    state.error = "";
    if (!preserveProgress) {
      state.stage = intent === "steer" ? "active" : "thinking";
      state.guidance = intent === "steer"
        ? "补充引导已送达，智体会按最新要求继续当前任务。"
        : "任务已开始，正在理解目标与工作区上下文。";
      state.detail = intent === "steer" ? "当前任务标识保持不变" : "";
    }
    this.emitIfSelected(threadId);
  }

  submissionFailed(threadId: string, message: string): void {
    const state = this.state(threadId);
    state.submissionPending = false;
    state.error = message;
    state.stage = "recoverableError";
    state.guidance = state.activeTurnId
      ? "补充引导发送失败，原任务仍在执行；内容已恢复，可修改后重试。"
      : "任务没有成功开始；内容已恢复，可修改后重试。";
    state.detail = message;
    this.emitIfSelected(threadId);
  }

  submissionConfirmed(threadId: string): void {
    const state = this.state(threadId);
    if (!state.submissionPending) return;
    state.submissionPending = false;
    state.error = "";
    if (state.activeTurnId) {
      state.guidance = "服务器已收到补充引导，当前任务继续执行。";
      state.detail = "等待后续任务事件";
    } else {
      state.stage = "starting";
      state.guidance = "服务器已收到请求，正在等待任务开始事件。";
      state.detail = "输入已由服务器确认";
    }
    this.emitIfSelected(threadId);
  }

  beginResync(threadId: string): void {
    const state = this.state(threadId);
    state.submissionPending = true;
    state.interruptPending = false;
    state.interruptAccepted = false;
    state.stage = "starting";
    state.guidance = "正在从服务器恢复任务状态。";
    state.detail = "同步完成前不会发送新的工作指令";
    state.error = "";
    this.emitIfSelected(threadId);
  }

  resyncFailed(threadId: string, message: string): void {
    const state = this.state(threadId);
    state.activeTurnId = null;
    state.submissionPending = false;
    state.interruptPending = false;
    state.interruptAccepted = false;
    state.stage = "recoverableError";
    state.guidance = "服务器任务状态恢复失败；发送前请确认连接，或重新选择该任务。";
    state.detail = message;
    state.error = message;
    this.emitIfSelected(threadId);
  }

  abandonResync(threadId: string): void {
    const state = this.state(threadId);
    state.activeTurnId = null;
    state.submissionPending = false;
    state.interruptPending = false;
    state.interruptAccepted = false;
    state.stage = "idle";
    state.guidance = "已解除上一次结果待确认状态；原内容仍保留在输入框中。";
    state.detail = "如果服务器稍后执行了旧请求，请先核对任务记录，避免重复操作。";
    state.error = "";
    this.emitIfSelected(threadId);
  }

  beginInterrupt(threadId: string): boolean {
    const state = this.state(threadId);
    if (!state.activeTurnId || state.interruptPending) return false;
    state.interruptPending = true;
    state.interruptAccepted = false;
    state.stage = "stopping";
    state.guidance = "正在发送停止请求；任务尚未确认结束。";
    state.detail = "等待服务器返回最终任务状态";
    this.emitIfSelected(threadId);
    return true;
  }

  interruptRequestAccepted(threadId: string, expectedTurnId: string): void {
    const state = this.state(threadId);
    if (!state.interruptPending || state.activeTurnId !== expectedTurnId) return;
    state.interruptAccepted = true;
    state.stage = "stopping";
    state.guidance = "停止请求已送达，正在等待任务安全结束。";
    state.detail = "收到最终状态前不会重复发送停止请求";
    this.emitIfSelected(threadId);
  }

  interruptRequestFailed(threadId: string, expectedTurnId: string, message: string): void {
    const state = this.state(threadId);
    if (!state.activeTurnId || state.activeTurnId !== expectedTurnId) return;
    state.interruptPending = false;
    state.interruptAccepted = false;
    state.error = message;
    state.stage = "recoverableError";
    state.guidance = "停止请求失败，任务仍在进行；可以再次尝试停止。";
    state.detail = message;
    this.emitIfSelected(threadId);
  }

  markAwaitingApproval(threadId: string, turnId: string | null, method: string): void {
    const state = this.state(threadId);
    if (isTerminalTurn(state, turnId)) return;
    if (turnId && state.activeTurnId && state.activeTurnId !== turnId) return;
    if (turnId && !state.activeTurnId) {
      state.activeTurnId = turnId;
      state.terminalTurnId = null;
      state.terminalStatus = null;
      state.error = "";
    }
    const labels: Record<string, string> = {
      "item/commandExecution/requestApproval": "命令需要你的确认",
      "item/fileChange/requestApproval": "文件变更需要你的确认",
      "item/permissions/requestApproval": "权限请求需要你的确认",
      "item/tool/requestUserInput": "工具正在等待你的输入",
      "mcpServer/elicitation/request": "连接器正在等待你的输入",
    };
    state.stage = "waiting";
    state.guidance = labels[method] || "任务正在等待你的确认。";
    state.detail = "完成确认后任务会继续执行";
    state.awaitingApprovalMethod = method || "serverRequest";
    state.awaitingApprovalTurnId = turnId;
    state.lastActivityAt = Date.now();
    this.emitIfSelected(threadId);
  }

  approvalResolved(threadId: string, turnId: string | null = null): void {
    const state = this.state(threadId);
    if (isTerminalTurn(state, turnId)) return;
    if (turnId && state.awaitingApprovalTurnId && turnId !== state.awaitingApprovalTurnId) return;
    state.awaitingApprovalMethod = null;
    state.awaitingApprovalTurnId = null;
    state.lastActivityAt = Date.now();
    if (state.activeTurnId) {
      state.stage = "active";
      state.guidance = "确认已处理，任务正在继续。";
      state.detail = "正在等待服务器返回下一条进度";
    }
    this.emitIfSelected(threadId);
  }

  syncThreadHistory(threadId: string, turns: WorkEventData[]): void {
    const state = this.state(threadId);
    for (const turn of turns) {
      const turnId = stringValue(turn.id);
      if (turnId && terminalStatus(turn.status)) rememberTerminalTurn(state, turnId);
    }
    const latest = [...turns].reverse().find((turn) => (
      ["inProgress", "in_progress", "running"].includes(stringValue(turn.status))
      || terminalStatus(turn.status)
    ));
    const latestTurnId = stringValue(latest?.id);
    const latestStatus = terminalStatus(latest?.status);
    state.activities.clear();
    state.lastActivityAt = Date.now();
    if (latest && !latestStatus && latestTurnId) {
      state.activeTurnId = latestTurnId;
      state.terminalTurnId = null;
      state.submissionPending = false;
      state.interruptPending = false;
      state.interruptAccepted = false;
      const approvalStillPending = Boolean(
        state.awaitingApprovalMethod
        && (!state.awaitingApprovalTurnId || state.awaitingApprovalTurnId === latestTurnId),
      );
      state.stage = approvalStillPending ? "waiting" : "active";
      state.guidance = approvalStillPending ? "任务正在等待你的确认。" : "已恢复正在进行的服务器任务。";
      state.detail = approvalStillPending ? "点击“查看并确认”后任务才会继续" : "此时发送会作为补充引导进入当前任务";
      state.terminalStatus = null;
      state.error = "";
    } else {
      state.activeTurnId = null;
      state.submissionPending = false;
      state.interruptPending = false;
      state.interruptAccepted = false;
      if (latestStatus) {
        state.awaitingApprovalMethod = null;
        state.awaitingApprovalTurnId = null;
        state.terminalTurnId = latestTurnId || null;
        this.setTerminalState(state, latestStatus, errorMessage(record(latest?.error)));
      }
      else {
        state.awaitingApprovalMethod = null;
        state.awaitingApprovalTurnId = null;
        state.terminalTurnId = null;
        state.terminalStatus = null;
        state.stage = "idle";
        state.guidance = "输入指令后将开始一个新任务。";
        state.detail = "";
        state.error = "";
      }
    }
    this.emitIfSelected(threadId);
  }

  applyEvent(topic: string, data: WorkEventData): void {
    const turn = record(data.turn);
    const item = record(data.item);
    const threadId = stringValue(data.threadId) || stringValue(turn?.threadId) || this.threadIdForTurn(
      stringValue(data.turnId) || stringValue(turn?.id),
    );
    const turnId = stringValue(data.turnId) || stringValue(turn?.id);

    if (topic === "turn/started" || topic === "turn.started" || topic === "codex.turn.started") {
      if (!threadId || !turnId) return;
      const state = this.state(threadId);
      if (isTerminalTurn(state, turnId)) return;
      state.activeTurnId = turnId;
      state.terminalTurnId = null;
      state.submissionPending = false;
      state.interruptPending = false;
      state.interruptAccepted = false;
      state.stage = "thinking";
      state.guidance = "任务已开始，正在理解目标与工作区上下文。";
      state.detail = "";
      state.terminalStatus = null;
      state.error = "";
      state.awaitingApprovalMethod = null;
      state.awaitingApprovalTurnId = null;
      state.lastActivityAt = Date.now();
      this.emitIfSelected(threadId);
      return;
    }

    if (topic === "turn/completed" || topic === "turn.completed" || topic === "codex.turn.completed") {
      const status = terminalStatus(turn?.status ?? data.status);
      if (!status) return;
      const resolvedThreadId = threadId || this.threadIdForTurn(turnId);
      if (!resolvedThreadId) return;
      const state = this.state(resolvedThreadId);
      const completedTurnId = turnId || state.activeTurnId;
      const alreadyTerminal = isTerminalTurn(state, completedTurnId);
      if (completedTurnId) rememberTerminalTurn(state, completedTurnId);
      if (turnId && state.activeTurnId && state.activeTurnId !== turnId) return;
      if (turnId && !state.activeTurnId && alreadyTerminal) return;
      state.activeTurnId = null;
      state.terminalTurnId = completedTurnId || null;
      state.submissionPending = false;
      state.interruptPending = false;
      state.interruptAccepted = false;
      state.activities.clear();
      state.awaitingApprovalMethod = null;
      state.awaitingApprovalTurnId = null;
      state.lastActivityAt = Date.now();
      this.setTerminalState(state, status, errorMessage(turn?.error ?? data.error));
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    const resolvedThreadId = threadId || this.threadIdForTurn(turnId);
    if (!resolvedThreadId) return;
    const state = this.state(resolvedThreadId);
    if (state.activeTurnId) state.lastActivityAt = Date.now();
    if (isTerminalTurn(state, turnId)) return;
    if (turnId && state.activeTurnId && state.activeTurnId !== turnId) return;
    if (turnId && !state.activeTurnId && ACTIVE_TURN_EVIDENCE_TOPICS.has(topic)) {
      state.activeTurnId = turnId;
      state.terminalTurnId = null;
      state.terminalStatus = null;
      state.error = "";
    }

    if (topic === "turn/plan/updated" || topic === "turn.plan.updated" || topic === "codex.plan.updated") {
      state.plan = normalizePlan(data.plan ?? data.steps);
      const current = state.plan.find((step) => step.status === "inProgress")
        || state.plan.find((step) => step.status === "pending");
      state.stage = "planning";
      state.guidance = current ? `当前步骤：${current.step}` : "执行计划已更新。";
      state.detail = concise(data.explanation) || "计划会随任务进展动态更新";
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "item/started" || topic === "codex.item.started") {
      if (!item) return;
      const activity = activityFor(item);
      if (!activity) return;
      state.activities.set(activity.id, activity);
      this.applyTopActivity(state);
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "item/completed" || topic === "codex.item.completed") {
      if (!item) return;
      const activity = activityFor(item);
      if (activity) state.activities.delete(activity.id);
      const itemStatus = stringValue(item.status);
      if (itemStatus === "failed" || itemStatus === "declined") {
        state.stage = "active";
        state.guidance = itemStatus === "declined"
          ? "这一项操作未获允许，任务正在根据结果继续处理。"
          : "这一项操作没有成功，任务正在决定后续处理。";
        state.detail = errorMessage(item.error);
      } else if (item.type === "agentMessage" && item.phase === "final_answer") {
        state.stage = "responding";
        state.guidance = "最终结果已生成，正在确认任务结束状态。";
        state.detail = "";
      } else {
        this.applyTopActivity(state);
      }
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (["item/commandExecution/outputDelta", "item/commandExecution/terminalInteraction", "command/exec/outputDelta"].includes(topic)) {
      state.stage = "command";
      state.guidance = "后台命令正在运行。";
      state.detail = "输出会按服务器事件顺序实时更新";
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (["turn/diff/updated", "item/fileChange/patchUpdated"].includes(topic)) {
      state.stage = "files";
      state.guidance = "文件变更正在更新。";
      state.detail = "以服务器返回的文件项与最终差异为准";
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "item/mcpToolCall/progress") {
      state.stage = "tool";
      state.guidance = "工具正在执行。";
      state.detail = concise(data.message ?? data.status);
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "mcpServer/startupStatus/updated") {
      const status = String(data.status || "").toLowerCase();
      if (!["failed", "error"].includes(status) && !data.error && !data.failureReason) return;
      state.stage = "recoverableError";
      state.guidance = "MCP 服务启动失败。";
      state.detail = concise(data.error ?? data.failureReason ?? data.message ?? data.status);
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "thread/status/changed") {
      const status = record(data.status) || data;
      const activeFlags = Array.isArray(status.activeFlags) ? status.activeFlags.map(String) : [];
      if (activeFlags.includes("waitingOnApproval")) {
        state.awaitingApprovalMethod ||= "serverRequest";
        state.awaitingApprovalTurnId ||= state.activeTurnId;
        state.stage = "waiting";
        state.guidance = "任务正在等待你的确认。";
        state.detail = "确认内容正在从服务器同步";
      } else if (state.stage === "waiting" && state.activeTurnId && !state.awaitingApprovalMethod) {
        state.stage = "active";
        state.guidance = "确认已处理，任务正在继续。";
        state.detail = "";
      }
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "model/safetyBuffering/updated") {
      if (data.showBufferingUi === true) {
        state.stage = "thinking";
        state.guidance = "正在完成安全检查。";
        state.detail = "服务器尚未确认输出可展示";
      } else if (state.activeTurnId) {
        state.stage = "active";
        state.guidance = "安全检查已完成，任务正在继续。";
        state.detail = "";
      }
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "model/rerouted") {
      state.stage = "active";
      state.guidance = "服务器已切换执行模型。";
      state.detail = concise(data.reason ?? data.model ?? data);
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "model/verification") {
      state.stage = "waiting";
      state.guidance = "账户需要完成服务器验证。";
      state.detail = concise(data.message ?? data.reason) || "完成验证后任务才能继续";
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "error") {
      state.stage = "recoverableError";
      state.guidance = "任务出现错误，正在等待服务器给出最终状态。";
      state.detail = errorMessage(data.error);
      this.emitIfSelected(resolvedThreadId);
      return;
    }

    if (topic === "serverRequest/resolved" && state.stage === "waiting") {
      state.awaitingApprovalMethod = null;
      state.awaitingApprovalTurnId = null;
      state.stage = "active";
      state.guidance = "确认已处理，任务正在继续。";
      state.detail = "";
      this.emitIfSelected(resolvedThreadId);
    }
  }

  snapshot(threadId = this.selectedThreadId): WorkControlSnapshot {
    const state = threadId ? this.state(threadId) : emptyState();
    const isActive = Boolean(state.activeTurnId);
    const composerIntent = isActive ? "steer" : "start";
    const stopLabel = state.interruptPending ? "停止中" : "停止";
    return {
      threadId,
      activeTurnId: state.activeTurnId,
      isActive,
      submissionPending: state.submissionPending,
      interruptPending: state.interruptPending,
      canStop: isActive && !state.interruptPending,
      composerIntent,
      composerPlaceholder: isActive ? "补充引导当前任务…" : "告诉彼岸花·智体接下来要完成什么…",
      composerHint: isActive
        ? "任务进行中：此时发送会作为补充引导进入当前任务，不会创建新任务。"
        : "发送后将开始一个新的任务轮次。",
      sendLabel: state.submissionPending ? "正在发送" : isActive ? "发送补充引导" : "发送工作指令",
      stopLabel,
      stage: state.stage,
      stageLabel: STAGE_LABELS[state.stage],
      guidance: state.guidance,
      detail: state.detail,
      plan: state.plan.map((step) => ({ ...step })),
      terminalStatus: state.terminalStatus,
      error: state.error,
      awaitingApproval: Boolean(state.awaitingApprovalMethod),
      lastActivityAt: state.lastActivityAt,
    };
  }

  private state(threadId: string): ThreadState {
    let state = this.threads.get(threadId);
    if (!state) {
      state = emptyState();
      this.threads.set(threadId, state);
    }
    return state;
  }

  private threadIdForTurn(turnId: string): string {
    if (!turnId) return "";
    for (const [threadId, state] of this.threads) {
      if (state.activeTurnId === turnId || state.terminalTurnIds.has(turnId)) return threadId;
    }
    return "";
  }

  private applyTopActivity(state: ThreadState): void {
    const top = [...state.activities.values()]
      .sort((left, right) => ACTIVITY_PRIORITY[right.stage] - ACTIVITY_PRIORITY[left.stage])[0];
    if (top) {
      state.stage = top.stage;
      state.guidance = STAGE_LABELS[top.stage];
      state.detail = top.detail;
    } else if (state.activeTurnId) {
      state.stage = "active";
      state.guidance = "正在继续执行当前任务。";
      state.detail = "你可以随时发送补充引导";
    }
  }

  private setTerminalState(state: ThreadState, status: WorkTerminalStatus, message: string): void {
    state.terminalStatus = status;
    state.error = status === "failed" ? message : "";
    state.stage = status;
    if (status === "completed") {
      state.guidance = "服务器已确认任务完成，可以继续输入新任务。";
      state.detail = "";
    } else if (status === "interrupted") {
      state.guidance = "服务器已确认任务停止；已有输出和文件变更仍会保留。";
      state.detail = "";
    } else {
      state.guidance = "服务器已确认任务失败，可以修改指令后重新发送。";
      state.detail = message;
    }
  }

  private emitIfSelected(threadId: string): void {
    if (this.selectedThreadId === threadId) this.emit();
  }

  private emit(): void {
    const snapshot = this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}
